import { IDeviceAdapter, PersonData, DeviceOperation, DeviceInfo } from './base';
import {
  SessionInfo,
  SessionEstablishmentRequest,
  SessionEstablishmentResult,
} from '../session/types';

/**
 * Generic RFID Adapter (placeholder)
 * Can be extended for specific RFID device types
 *
 * NOTE: This is a stub implementation. Actual RFID devices may require
 * different payload structures depending on the manufacturer.
 */
export class RFIDAdapter implements IDeviceAdapter {
  vendor = 'rfid' as const;

  /**
   * Format person sync payload for RFID device
   * Generic format - implement based on actual device requirements
   */
  formatSyncPersonPayload(personData: PersonData): any {
    const fullName = `${personData.first_name} ${personData.last_name}`.trim();

    // Generic RFID payload - adapt based on actual device
    return {
      person_id: personData.person_id,
      name: fullName,
      card_number: personData.cpf, // Or from RFID-specific field
      enabled: personData.isAllowed !== false,
      valid_from: '2020-01-01T00:00:00',
      valid_until: '2030-12-31T23:59:59',
    };
  }

  /**
   * Format person removal payload
   */
  formatRemovePersonPayload(devicePersonId: string): any {
    return {
      person_id: devicePersonId,
    };
  }

  /**
   * Format face photo upload payload
   * Generic RFID devices typically don't have face recognition
   */
  formatUploadFacePayload(employeeNo: string, photoBase64: string): any {
    // RFID devices typically don't support face upload
    // Return empty payload or throw error
    console.warn('[RFID] Face upload not supported for generic RFID devices');
    return {};
  }

  /**
   * Generate device person ID from PersonData
   * RFID uses person_id directly
   */
  generateDevicePersonId(personData: PersonData): string {
    return personData.person_id;
  }

  /**
   * Format load people payload for RFID device
   */
  formatLoadPeoplePayload(): any {
    return {};
  }

  /**
   * Parse RFID load people response to common format
   */
  parseLoadPeopleResponse(responseData: any): Array<{
    employeeNo: string;
    name: string;
    userType: string;
  }> {
    const usersArray = responseData?.users || (Array.isArray(responseData) ? responseData : []);

    return usersArray.map((user: any) => ({
      employeeNo: user.person_id?.toString() || user.card_number?.toString() || '',
      name: user.name?.toString() || '',
      userType: user.userType?.toString() || 'normal',
    }));
  }

  /**
   * Get endpoint for operation
   */
  getEndpoint(operation: DeviceOperation, device: DeviceInfo): string {
    const ip = device.ip_address || device.local_ip_address;
    if (!ip) throw new Error('No IP address available for device');

    const cleanIp = ip.replace(/^https?:\/\//, '');
    let deviceIp = cleanIp;
    let port = device.port || '80';
    if (cleanIp.includes(':')) {
      const parts = cleanIp.split(':');
      deviceIp = parts[0];
      port = parts[1];
    }

    const protocol = port === '443' ? 'https' : 'http';
    const base = `${protocol}://${deviceIp}:${port}/api`;

    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        return `${base}/users/add`;
      case DeviceOperation.REMOVE_PERSON:
        return `${base}/users/remove`;
      case DeviceOperation.UPLOAD_FACE:
        return `${base}/users/update_photo`;
      case DeviceOperation.CHECK_HEALTH:
        return `${base}/status`;
      case DeviceOperation.LOAD_PEOPLE:
        return `${base}/users/list`;
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
  }

  /**
   * Get HTTP method for operation
   */
  getMethod(operation: DeviceOperation): 'GET' | 'POST' | 'PUT' | 'DELETE' {
    // Generic defaults - adapt based on actual device API
    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        return 'POST';
      case DeviceOperation.REMOVE_PERSON:
        return 'POST';
      case DeviceOperation.UPLOAD_FACE:
        return 'POST';
      case DeviceOperation.CHECK_HEALTH:
        return 'GET';
      case DeviceOperation.LOAD_PEOPLE:
        return 'GET';
      default:
        return 'POST';
    }
  }

  // ==================== SESSION AUTHENTICATION METHODS ====================

  /**
   * Check if device requires session-based authentication
   * Generic RFID devices typically don't require session auth
   */
  async requiresSessionAuth(device: DeviceInfo): Promise<boolean> {
    return false; // Most RFID devices use basic/digest auth
  }

  /**
   * Detect firmware version (not needed for RFID)
   */
  async detectFirmwareVersion?(device: DeviceInfo): Promise<null> {
    return null;
  }

  /**
   * Establish session (not needed for RFID)
   */
  async establishSession(
    request: SessionEstablishmentRequest
  ): Promise<SessionEstablishmentResult> {
    return {
      success: false,
      fallbackToDigest: true,
      error: 'Session auth not supported for RFID devices',
    };
  }

  /**
   * Format URL with session parameters (no-op for RFID)
   */
  formatUrlWithSession(endpoint: string, session: SessionInfo): string {
    return endpoint;
  }

  /**
   * Format headers with session information (no-op for RFID)
   */
  formatHeadersWithSession(
    baseHeaders: Record<string, string>,
    session: SessionInfo
  ): Record<string, string> {
    return baseHeaders;
  }

  /**
   * Check if session is still valid (always false for RFID)
   */
  isSessionValid(session: SessionInfo): boolean {
    return false;
  }
}
